import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl, type MessageValue } from 'react-intl';

import { type TabItem } from '@garnish/layout';
import { useSelector } from '@xstate/react';

import { type LocationSearchService, type PredictionsListTab } from '../../../../machine';

// ─────────────────────────────────────────────────────────────────────────────

export const useLocationSearchPredictionsTabs = (params: Params) => {
  const { locationSearchActorRef, predictionsCount, locationsCount } = params;

  const { send } = locationSearchActorRef;

  const { formatMessage } = useIntl();

  // ─── State ───────────────────────────────────────────────────────────

  const locationPredictionTabs = useSelector(
    locationSearchActorRef,
    (state) => state.context.predictionTabs,
  );

  const activeTabId = useSelector(
    locationSearchActorRef,
    (state) => state.context.activePredictionTab,
  );

  // ─── Flags ───────────────────────────────────────────────────────────

  const hasPredictions = predictionsCount > 0;
  const hasLocations = locationsCount > 0;
  const shouldRenderTabBar = hasPredictions || hasLocations;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const setActiveTabId = useCallback(
    (tabId: PredictionsListTab) => {
      if (tabId === 'location-prediction-tab.predictions') {
        send({ type: 'SET_PREDICTION_TAB_TO_PREDICTIONS' });
      }

      if (tabId === 'location-prediction-tab.locations') {
        send({ type: 'SET_PREDICTION_TAB_TO_LOCATIONS' });
      }
    },
    [send],
  );

  // ─────────────────────────────────────────────────────────────────────

  const tabs = useMemo<TabItem<PredictionsListTab>[]>(() => {
    const predictionsLabel = formatMessage(messages.locations, { count: predictionsCount }); // prettier-ignore
    const locationsLabel = formatMessage(messages.sgRestaurants, { count: locationsCount }); // prettier-ignore

    return locationPredictionTabs.map((tabId) => {
      const isPredictionsTab = tabId === 'location-prediction-tab.predictions';

      return {
        id: isPredictionsTab ? 'location-prediction-tab.predictions' : 'location-prediction-tab.locations', // prettier-ignore
        panelId: `location-prediction-tab-panel.${tabId}`,
        label: isPredictionsTab ? predictionsLabel : locationsLabel,
        isDisabled: isPredictionsTab ? !hasPredictions : !hasLocations,
      };
    });
  }, [
    formatMessage,
    hasLocations,
    hasPredictions,
    locationPredictionTabs,
    predictionsCount,
    locationsCount,
  ]);

  // ─────────────────────────────────────────────────────────────────────────────

  return {
    tabs,
    activeTabId,
    setActiveTabId,
    shouldRenderTabBar,
  };
};

// ─── Messages ─────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly locations: { readonly count: MessageValue };
  readonly sgRestaurants: { readonly count: MessageValue };
}>({
  locations: {
    defaultMessage: 'Locations ({count})',
    description: 'Location Search | Predictions | Locations title',
  },
  sgRestaurants: {
    defaultMessage: 'SG Restaurants ({count})',
    description: 'Location Search | Predictions | SG Restaurants title',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  locationSearchActorRef: LocationSearchService; // prettier-ignore
  predictionsCount: number;
  locationsCount: number;
};
